import { useDataProvider } from "@refinedev/core";
import { useQuery } from "@tanstack/react-query";

import type { MapVenue } from "@/pages/areas/components/area-map";

const PAGE_SIZE = 250;
const MAX_PAGES = 4;

const num = (v: unknown): number | undefined =>
  typeof v === "number" && Number.isFinite(v) ? v : undefined;

/** Typesense hands back `{latitude, longitude}` for Mood docs; accept `[lat, lng]` too. */
export const toMapVenue = (
  hit: { document?: Record<string, any> } | Record<string, any>,
  countryCode?: string,
): MapVenue | null => {
  // The default provider returns Typesense hits: `{document, highlights}`.
  const doc: Record<string, any> = hit.document ?? hit;
  // The collection has no country field to filter on, so same-name cities are dropped here.
  const country = doc.geodata?.countryCode;
  if (
    countryCode &&
    typeof country === "string" &&
    country &&
    country.toLowerCase() !== countryCode.toLowerCase()
  )
    return null;
  const g = doc.geolocation;
  const lat = Array.isArray(g) ? num(g[0]) : num(g?.latitude ?? g?.lat);
  const lng = Array.isArray(g) ? num(g[1]) : num(g?.longitude ?? g?.lng);
  if (lat === undefined || lng === undefined || !doc.id) return null;
  return {
    id: String(doc.id),
    name: String(doc.name ?? doc.id),
    lat,
    lng,
    hasArea: typeof doc.area === "string" && doc.area.trim() !== "",
  };
};

export interface CityVenuePins {
  pins: MapVenue[];
  /** Every venue the city has, so a map can say it shows only part of them. */
  total: number;
}

/**
 * The city's venues as map pins, through the default provider's venues `getList` (the `/explore`
 * search the venues list uses). Up to {@link MAX_PAGES} pages of {@link PAGE_SIZE}.
 */
export const useCityVenuePins = (
  city: string | undefined,
  countryCode?: string,
) => {
  const { getList } = useDataProvider()();
  return useQuery({
    queryKey: ["areas", "map-venues", city, countryCode],
    enabled: !!city,
    staleTime: 60_000,
    queryFn: async (): Promise<CityVenuePins> => {
      const pins: MapVenue[] = [];
      let fetched = 0;
      let serverTotal = 0;
      for (let page = 1; page <= MAX_PAGES; page++) {
        const res = await getList({
          resource: "venues",
          pagination: { currentPage: page, pageSize: PAGE_SIZE },
          filters: [{ field: "geodata.name", operator: "eq", value: city }],
          meta: {
            includeFields: [
              "id",
              "name",
              "area",
              "geolocation",
              "geodata.countryCode",
            ],
          },
        });
        fetched += res.data.length;
        serverTotal = res.total ?? 0;
        for (const doc of res.data as Record<string, any>[]) {
          const pin = toMapVenue(doc, countryCode);
          if (pin) pins.push(pin);
        }
        if (res.data.length < PAGE_SIZE) break;
      }
      // The server total only matters when the pages stopped short of it.
      return {
        pins,
        total: serverTotal > fetched ? serverTotal : pins.length,
      };
    },
  });
};
